import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import userRoleService from '../../services/userRoleService';

interface Props {
  profession: string;
  institutionName?: string;
  institutionCity?: string;
  institutionCountry?: string;
  institutionType?: string;
  subjectsTaught?: string;
  activatedRolesCount: number;
  onRemoved: () => void;
}

interface FormState {
  profession: string;
  institutionName: string;
  institutionCity: string;
  institutionCountry: string;
  institutionType: string;
  subjectsTaught: string;
}

const TeacherRoleSection: React.FC<Props> = ({
  profession: initialProfession,
  institutionName: initInstitutionName = '',
  institutionCity: initInstitutionCity = '',
  institutionCountry: initInstitutionCountry = '',
  institutionType: initInstitutionType = '',
  subjectsTaught: initSubjectsTaught = '',
  activatedRolesCount,
  onRemoved,
}) => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>({
    profession: initialProfession,
    institutionName: initInstitutionName,
    institutionCity: initInstitutionCity,
    institutionCountry: initInstitutionCountry,
    institutionType: initInstitutionType,
    subjectsTaught: initSubjectsTaught,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const isLastRole = activatedRolesCount <= 1;

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await userRoleService.updateRoleProfile('TEACHER', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await userRoleService.removeRole('TEACHER');
      onRemoved();
      if (isLastRole) navigate('/role-selection', { replace: true });
      else setRemoveDialogOpen(false);
    } catch (err: any) {
      setRemoveError(err.message || 'Failed to remove Teacher role');
      setRemoving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Teacher Role</Typography>

      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Profile saved.</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <TextField label="Profession" value={form.profession}
          onChange={e => setForm(f => ({ ...f, profession: e.target.value }))} />
        <TextField label="School / Institution Name" value={form.institutionName}
          onChange={e => setForm(f => ({ ...f, institutionName: e.target.value }))} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="City" value={form.institutionCity} sx={{ flex: 1 }}
            onChange={e => setForm(f => ({ ...f, institutionCity: e.target.value }))} />
          <TextField label="Country" value={form.institutionCountry} sx={{ flex: 1 }}
            onChange={e => setForm(f => ({ ...f, institutionCountry: e.target.value }))} />
        </Box>
        <TextField label="Institution Type" value={form.institutionType}
          placeholder="e.g. Secondary, Primary"
          onChange={e => setForm(f => ({ ...f, institutionType: e.target.value }))} />
        <TextField label="Subjects Taught" value={form.subjectsTaught}
          placeholder="e.g. Mathematics, Physics"
          onChange={e => setForm(f => ({ ...f, subjectsTaught: e.target.value }))} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          endIcon={saving ? <CircularProgress size={16} /> : null}>
          {saving ? 'Saving…' : 'Save Profile'}
        </Button>
        <Button variant="outlined" color="error" onClick={() => setRemoveDialogOpen(true)}>
          Remove Teacher Role
        </Button>
      </Box>

      <Dialog open={removeDialogOpen} onClose={() => !removing && setRemoveDialogOpen(false)} maxWidth="xs">
        <DialogTitle>Remove Teacher Role?</DialogTitle>
        <DialogContent>
          {removeError && <Alert severity="error" sx={{ mb: 2 }}>{removeError}</Alert>}
          <DialogContentText>
            Your Teacher profile data will be preserved and can be restored when you re-activate.
          </DialogContentText>
          {isLastRole && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This is your only active role. You will need to activate a role at your next sign-in.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)} disabled={removing}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRemove} disabled={removing}
            endIcon={removing ? <CircularProgress size={16} /> : null}>
            {removing ? 'Removing…' : 'Remove Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherRoleSection;
